/**
 * Client for `GET /galaxy/:ring/:system` (galaxy plan Milestone B) — the
 * one authenticated read GalaxyView needs. Kept separate from
 * `BackendClient` since it's a single simple GET, not part of the
 * game/action dispatch contract.
 */

import { ApiActionError, getApiUrl, type AccessTokenSource } from './backend-client';
import type { GalaxySlot } from '@spacegame/ui';
import type { EngineEvent, GameState, ShipKey } from '@spacegame/engine';

export class GalaxyClient {
  constructor(
    private readonly auth: AccessTokenSource,
    private readonly baseUrl: string = getApiUrl(),
  ) {}

  async getSystem(ring: number, system: number): Promise<GalaxySlot[]> {
    const token = await this.auth.getAccessToken();
    const res = await fetch(`${this.baseUrl}/galaxy/${ring}/${system}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      throw new Error(`Galaxie-Daten konnten nicht geladen werden (${res.status}).`);
    }
    return res.json() as Promise<GalaxySlot[]>;
  }

  async attackColony(
    ring: number,
    system: number,
    position: number,
    fleet: Partial<Record<ShipKey, number>>,
  ): Promise<{ state: GameState; events: EngineEvent[] }> {
    const token = await this.auth.getAccessToken();
    const res = await fetch(`${this.baseUrl}/npc/${ring}/${system}/${position}/attack`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ fleet }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null) as { message?: string } | null;
      throw new ApiActionError(body?.message ?? `Angriff konnte nicht gestartet werden (${res.status}).`);
    }
    return res.json();
  }

  async scoutColony(ring: number, system: number, position: number, probes: number): Promise<{ state: GameState; events: EngineEvent[] }> {
    const token = await this.auth.getAccessToken();
    const res = await fetch(`${this.baseUrl}/npc/${ring}/${system}/${position}/scout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ probes }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null) as { message?: string } | null;
      throw new ApiActionError(body?.message ?? `Spähaktion konnte nicht gestartet werden (${res.status}).`);
    }
    return res.json();
  }

  async attackPlayer(ring: number, system: number, position: number, fleet: Partial<Record<ShipKey, number>>): Promise<{ state: GameState; events: EngineEvent[] }> {
    const token = await this.auth.getAccessToken();
    const res = await fetch(`${this.baseUrl}/pvp/${ring}/${system}/${position}/attack`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ fleet }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null) as { message?: string } | null;
      throw new ApiActionError(body?.message ?? `Angriff konnte nicht gestartet werden (${res.status}).`);
    }
    return res.json();
  }

  async scoutPlayer(ring: number, system: number, position: number, probes: number): Promise<{ state: GameState; events: EngineEvent[] }> {
    const token = await this.auth.getAccessToken();
    const res = await fetch(`${this.baseUrl}/pvp/${ring}/${system}/${position}/scout`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ probes }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null) as { message?: string } | null;
      throw new ApiActionError(body?.message ?? `Spähaktion konnte nicht gestartet werden (${res.status}).`);
    }
    return res.json();
  }
}
